import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Grid} from 'react-bootstrap';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import ModalActiveUser from './ModalActiveUser';
import ModalAddEditUser from './ModalAddEditUser';
import ModalRemoveUser from './ModalRemoveUser';
import UserIcon from './UserIcon';
import UserManagementHeader from './UserManagementHeader';
import {PageLayout} from '../../components';
import {Feedback, UserRoleLevel} from '../../constants/dataEnum';
import {UserRoleIcons} from '../../constants/userManagementEnum';
import withIntl from '../../lib/withIntl';
import {hideLoading, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {fetchProtocols, getRunningProtocol} from '../../stores/business/protocolManagementBusiness';
import {logout} from '../../stores/business/sessionBusiness';
import {fetchUsers, fetchActiveUsers, getUserRoleLevel} from '../../stores/business/userManagementBusiness';
import './UserManagement.scss';

const mapStateToProps = (state) => ({
  sessionStateUser: state.sessionState.user,
  userManagementStateAvailableRoles: state.userManagementState.availableRoles,
  userManagementStateAvailableRoleRights: state.userManagementState.availableRoleRights,
});

const mapDispatchToProps = (dispatch) => ({
  appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
  appStateActionHideLoading: bindActionCreators(hideLoading, dispatch),
  appStateActionShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  protocolManagementBusinessFetchProtocols: bindActionCreators(fetchProtocols, dispatch),
  protocolManagementBusinessGetRunningProtocol: bindActionCreators(getRunningProtocol, dispatch),
  sessionBusinessLogout: bindActionCreators(logout, dispatch),
  userManagementBusinessFetchUsers: bindActionCreators(fetchUsers, dispatch),
  userManagementBusinessFetchActiveUsers: bindActionCreators(fetchActiveUsers, dispatch),
  userManagementBusinessGetUserRoleLevel: bindActionCreators(getUserRoleLevel, dispatch),
});

function UserManagementContainer(props) {
  const {
    sessionStateUser,
    userManagementStateAvailableRoles,
    userManagementStateAvailableRoleRights,
    appStateActionShowLoading,
    appStateActionHideLoading,
    appStateActionShowSnackbar,
    sessionBusinessLogout,
    userManagementBusinessFetchUsers,
    userManagementBusinessFetchActiveUsers,
    userManagementBusinessGetUserRoleLevel,
    protocolManagementBusinessFetchProtocols,
    protocolManagementBusinessGetRunningProtocol,
  } = props;

  const [newUserModalVisible, setNewUserModalVisible] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchUserValue, setSearchUserValue] = useState('');
  const [sortIsDesc, setSortIsDesc] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [removeUserModalVisible, setRemoveUserModalVisible] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeUserModalVisible, setActiveUserModalVisible] = useState(false);
  const [showSharingConfirmation, setShowSharingConfirmation] = useState(false);
  const [isExecutorUser, setIsExecutorUser] = useState(false);
  const [isSelectedUserOperator, setIsSelectedUserOperator] = useState(false);

  const doFetchUsers = async () => {
    const response = await userManagementBusinessFetchUsers();

    if (!response) {
      return;
    }

    let filteredUser = response;

    // show tessara super user only to user service and its own
    if (
      userManagementBusinessGetUserRoleLevel() !== UserRoleLevel.SERVICE &&
      userManagementBusinessGetUserRoleLevel() !== UserRoleLevel.TESSARA_SUPER_USER
    ) {
      filteredUser = response.filter(
        (user) => userManagementBusinessGetUserRoleLevel(user) !== UserRoleLevel.TESSARA_SUPER_USER,
      );
    }

    setAvailableUsers(filteredUser);
  };

  const doFetchActiveUsers = (userIds) => userManagementBusinessFetchActiveUsers(userIds);

  const isUserProtocolExecutor = async (userIds) => {
    const runningProtocolResponse = await protocolManagementBusinessGetRunningProtocol();
    let executor;

    if (runningProtocolResponse.code === 0) {
      const protocolExecutorUserId = runningProtocolResponse?.payload?.user?.userId;

      userIds.forEach((userId) => {
        if (protocolExecutorUserId === userId) {
          executor = runningProtocolResponse.payload.user;
        }
      });
    }

    return executor;
  };

  const isUserOperator = () => {
    const user = availableUsers.find((item) => item.userId === selectedUserIds[0]);
    let isOperator = false;

    if (user.userRole === '4') {
      isOperator = true;
    }
    setIsSelectedUserOperator(isOperator);
    return isOperator;
  };

  const isSelectedUserTessara = () => {
    const user = availableUsers.find((item) => item.userId === selectedUserIds[0]);
    let isTessara = false;

    if (user.userRole === '5') {
      isTessara = true;
    }
    return isTessara;
  };

  // component did mount
  useEffect(() => {
    const init = async () => {
      await doFetchUsers();
    };

    init();
    // eslint-disable-next-line
  }, []);

  const newUserClick = () => {
    setInitialData({isActive: true, userRole: '3'});
    setIsEdit(false);
    setNewUserModalVisible(true);
  };

  const newUserModalOnClose = () => {
    setNewUserModalVisible(false);
  };

  const newUserModalOnSave = async (success) => {
    if (success) {
      await doFetchUsers();
      setNewUserModalVisible(false);

      return;
    }

    setNewUserModalVisible(false);
    // FIX ME does not have privilage
    sessionBusinessLogout();
  };

  const searchUserOnChange = (e) => {
    setSearchUserValue(e.target.value);
  };

  const sortByClick = () => {
    setSortIsDesc(!sortIsDesc);
  };

  const removeClick = async () => {
    try {
      appStateActionShowLoading();

      const response = await doFetchActiveUsers(selectedUserIds);
      const isExecutor = await isUserProtocolExecutor(selectedUserIds);
      const isOperator = isUserOperator();

      if (!response && isExecutor) {
        setRemoveUserModalVisible(true);
        appStateActionHideLoading();

        return;
      }

      let activeNames = [];

      for (let i = 0, ii = response.length; i < ii; i += 1) {
        const user = response[i];

        activeNames.push(user.name);
      }

      if (!isExecutor && activeNames.length === 0) {
        const protocolResponse = await protocolManagementBusinessFetchProtocols(selectedUserIds[0]);

        if (!isOperator) setShowSharingConfirmation(protocolResponse && protocolResponse.length > 0);
        setRemoveUserModalVisible(true);
        appStateActionHideLoading();

        return;
      }

      if (isExecutor && activeNames.length === 0) {
        setIsExecutorUser(true);
        activeNames = [isExecutor.name];
      }

      setActiveUsers(activeNames);
      setActiveUserModalVisible(true);
    } catch (error) {
      console.error(error);
    }

    appStateActionHideLoading();
  };

  const existActiveUserOnConfirmation = (activeNames) => {
    if (activeNames != null) {
      setActiveUsers(activeNames);
    }
    setActiveUserModalVisible(true);
  };

  const removeUserModalOnClose = async (response, targetUserId) => {
    setRemoveUserModalVisible(false);

    if (!response || (response.code !== 0 && response.code !== 3)) {
      appStateActionHideLoading();

      return;
    }

    // does not have privilage
    if (response.code === 3) {
      appStateActionHideLoading();

      // FIX ME does not have privilage
      sessionBusinessLogout();

      return;
    }

    setSelectedUserIds([]);
    await doFetchUsers();

    appStateActionHideLoading();

    let successRemoveMessage;

    if (!targetUserId) {
      successRemoveMessage = <FormattedHTMLMessage id="userManagement.removeUser.success" />;
    } else {
      const {name} = availableUsers.filter((e) => e.userId.toString() === targetUserId.toString())[0];

      successRemoveMessage = (
        <FormattedHTMLMessage id="userManagement.removeUser.transferOwnership.success" values={{name}} />
      );
    }

    appStateActionShowSnackbar(Feedback.SUCCESS, successRemoveMessage, 2000);
  };

  const activeUserModalOnClose = () => {
    setActiveUsers([]);
    setActiveUserModalVisible(false);
  };

  const editClick = (e, id) => {
    const toEditUser = _.find(availableUsers, {userId: id || selectedUserIds[0]});

    setInitialData(toEditUser);
    setIsEdit(true);
    setNewUserModalVisible(true);
  };

  const onUserIconClick = (id) => {
    const index = selectedUserIds.indexOf(id);
    const newSelectedUserIds = index < 0 ? [id] : [];

    setSelectedUserIds(newSelectedUserIds);
  };

  const onUserIconDoubleClick = (id) => {
    const index = selectedUserIds.indexOf(id);
    const newSelectedUserIds = [...selectedUserIds];

    if (index < 0) {
      newSelectedUserIds.push(id);
      setSelectedUserIds(newSelectedUserIds);
    }

    editClick(null, id);
  };

  const generateUserGlyph = (userRole) => {
    const getRoleUser = _.find(userManagementStateAvailableRoles, {id: userRole});

    const defaultIconKey = 'Regular User';
    const defaultIconName = 'icon-Regular-User';
    const icon = UserRoleIcons.find((e) => e.key === (getRoleUser ? getRoleUser.name : defaultIconKey));

    return icon ? icon.name : defaultIconName;
  };

  const isUserSelected = (id) => selectedUserIds.indexOf(id) >= 0;

  const getSortedAvailableUsers = () => {
    const sortedAvailableUsers = _.sortBy(availableUsers, [(user) => user.name.toLowerCase()]);

    return !sortIsDesc ? sortedAvailableUsers : sortedAvailableUsers.reverse();
  };

  const generateUserIcons = () => {
    const {userId} = sessionStateUser;

    const icons = [];
    const sortedAvailableUsers = getSortedAvailableUsers();

    for (let i = 0, ii = sortedAvailableUsers.length; i < ii; i += 1) {
      const u = sortedAvailableUsers[i];
      const name = u.name.toLowerCase();

      if (searchUserValue) {
        const keyword = searchUserValue.toLowerCase();

        if (name.indexOf(keyword) === -1) {
          continue;
        }
      }

      // hide service user
      if (u.userRole === '1') {
        continue;
      }

      // hide logged in user
      // so that the user cant remove themselves
      if (u.userId === userId) {
        continue;
      }

      icons.push(
        <UserIcon
          key={u.userId}
          id={u.userId}
          name={u.name}
          glyph={generateUserGlyph(u.userRole)}
          isActive={u.isActive}
          selected={isUserSelected(u.userId)}
          onClick={onUserIconClick}
          onDoubleClick={onUserIconDoubleClick}
        />,
      );
    }

    return icons;
  };

  // exclude service role from new user selection
  const generateSelectableRoles = () => {
    let availRole = userManagementStateAvailableRoles.filter((item) => item.level !== UserRoleLevel.SERVICE);

    if (initialData.userRole - 1 !== UserRoleLevel.TESSARA_SUPER_USER && isEdit) {
      availRole = availRole.filter((item) => item.level !== UserRoleLevel.TESSARA_SUPER_USER);
    }

    if (!isEdit) {
      const tessaraSuperUserIdx = availRole.findIndex((item) => item.level === UserRoleLevel.TESSARA_SUPER_USER);
      availRole.splice(tessaraSuperUserIdx);
    }

    return availRole;
  };

  return (
    <PageLayout>
      <PageLayout.Header>
        <UserManagementHeader
          searchUserValue={searchUserValue}
          sortIsDesc={sortIsDesc}
          removeDisabled={selectedUserIds.length !== 1 || isSelectedUserTessara()}
          editDisabled={selectedUserIds.length !== 1}
          newUserClick={newUserClick}
          searchUserOnChange={searchUserOnChange}
          sortByClick={sortByClick}
          removeClick={removeClick}
          editClick={editClick}
        />
      </PageLayout.Header>
      <PageLayout.Content>
        <div id="usermanagement-explorer">
          <Grid>
            <div className="usermanagement-container">
              <div className="usermanagement-explorer-list">{generateUserIcons()}</div>
            </div>
          </Grid>
        </div>
      </PageLayout.Content>
      {newUserModalVisible && (
        <ModalAddEditUser
          showModal={newUserModalVisible}
          onClose={newUserModalOnClose}
          onSave={newUserModalOnSave}
          isEdit={isEdit}
          isAccount={false}
          initialData={initialData}
          availableRoles={generateSelectableRoles()}
          availableRoleRights={userManagementStateAvailableRoleRights}
        />
      )}
      {removeUserModalVisible && (
        <ModalRemoveUser
          showModal={removeUserModalVisible}
          onClose={removeUserModalOnClose}
          selectedUserId={selectedUserIds[0]}
          availableUsers={availableUsers}
          showSharingConfirmation={showSharingConfirmation}
          onActiveUserCallback={existActiveUserOnConfirmation}
          isOperator={isSelectedUserOperator}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
      {activeUserModalVisible && (
        <ModalActiveUser
          showModal={activeUserModalVisible}
          onClose={activeUserModalOnClose}
          activeNames={activeUsers}
          isExecutorUser={isExecutorUser}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    </PageLayout>
  );
}

UserManagementContainer.propTypes = {
  sessionStateUser: PropTypes.object.isRequired,
  userManagementStateAvailableRoles: PropTypes.array.isRequired,
  userManagementStateAvailableRoleRights: PropTypes.array.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  appStateActionHideLoading: PropTypes.func.isRequired,
  appStateActionShowSnackbar: PropTypes.func.isRequired,
  sessionBusinessLogout: PropTypes.func.isRequired,
  userManagementBusinessFetchUsers: PropTypes.func.isRequired,
  userManagementBusinessFetchActiveUsers: PropTypes.func.isRequired,
  userManagementBusinessGetUserRoleLevel: PropTypes.func.isRequired,
  protocolManagementBusinessFetchProtocols: PropTypes.func.isRequired,
  protocolManagementBusinessGetRunningProtocol: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

UserManagementContainer.defaultProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(UserManagementContainer));
